import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useLocation }                   from 'react-router-dom';
import { useApolloClient, useMutation }              from '@apollo/react-hooks';

import { CorvalMutation, CorvalProjectTemplate, CorvalProjectVersion, Uuid } from '../types';
import { RouterUtility, runIfFunction }                                      from '../util';
import { DuplicateProjectDialog }                                            from '../views/pages/projects/operations/DuplicateProjectDialog';
import { QueryDialogProps }                                                  from '../views/components/from-query/QueryOpDialog';
import { usePrefStates }                                                     from './usePrefStates';
import { useSystemModals, useSystemToasts }                                  from './useSystem';
import { ProjectQueries }                                                    from '../queries/ProjectQueries';
import { CompareProjectDialog }                                              from '../views/pages/projects/operations/CompareProjectDialog';
import { ProjectService }                                                    from "@src/services";
import { DecisionEngineQueries }                                             from "@src/queries";
import { ProjectContext }                                                    from "@src/views/pages/projects/ProjectIndexPage";
import { CreateProjectVersionDialog }                                        from "@src/views/pages/projects/operations/CreateProjectVersionDialog";
import { ManageProjectVersionsDialog }                                       from "@src/views/pages/projects/operations/ManageProjectVersionsDialog";
import { ProjectTemplateManagementDialog }                                   from "@src/views/pages/projects/operations/ProjectTemplateManagementDialog";
import { CreateProjectDialog }                                               from "@src/views/pages/projects/operations/CreateProjectDialog";
import { ProjectTemplatesManagementDialog }                                  from "@src/views/pages/projects/operations/ProjectTemplatesManagementDialog";
import { ManageProjectVersionMilestonesDialog }                              from "@src/views/pages/projects/operations/ManageProjectVersionMilestonesDialog";


interface UseProjectHook {

    /* Navigates the user to a project, preferably using the last section / subsection */
    openProject( projectId: Uuid ): void,

    /* triggers the duplicate project dialog */
    duplicateProject( projectId?: Uuid, projectName?: string, onComplete?: QueryDialogProps['onComplete'] ): void,

    /* triggers a project comparison dialog */
    compareProjects( projectIds: Uuid[], title?: string, primaryProjectId?: Uuid ): void,

    /* Returns a planning page route for the given project */
    getPlanningPageRoute( projectId: Uuid ): string,

    /* send user to a subsection within a section */
    redirectToSubSectionWithin( routePathKey: string ): void,

    /* Updates the subpath section user should return to within a section */
    updateLastSubSection( routePathKey: string, routePath: string ): void,

    /* Updates the root section user should return to, and it's subpath */
    updateRootAndSubSection( routeBasePath: string, routeSubsectionPath: string ): void,

    /* Clears the root section user should return to */
    clearRootSection(): void,

    /* Gets a project version object by id, or returns the default version if not found;
     Requires a project version array to be present. Uses the current preference state for referring
     to the last known project version id for a given project a user was looking at. */
    getMaybeCurrentProjectVersionFromSet( projectVersions: any[] ): Partial<CorvalProjectVersion> | null;

    /* triggers a project version creation dialog */
    createNewProjectVersion( projectId: Uuid, projectVersionId: Uuid, onComplete?: ( newProjectVersionId: Uuid ) => void ): void,

    /* triggers a project version management tool dialog */
    showProjectVersionManagementDialog( projectId: Uuid ): void,

    /* triggers a project template creation dialog */
    showProjectTemplateCreationDialog( projectId: Uuid, projectVersionId: Uuid, onComplete?: () => void ): void,

    /* triggers all project template edit dialog */
    showProjectTemplatesManagementDialog( onComplete?: ( templateListing: CorvalProjectTemplate[] ) => void ): void,

    /* triggers a project template edit dialog */
    showProjectTemplateEditDialog( templateId: Uuid, onComplete?: () => void ): void,

    /* triggers a new project from template dialog */
    showCreateProjectFromTemplateDialog( onComplete?: () => void ): void,

    /* triggers a project version milestones edit dialog */
    showProjectVersionMilestoneManagementDialog( projectId: Uuid, projectVersionId: Uuid, noopMutation?: boolean, onComplete?: () => void ): void,
}

export const useProject = ( projectId: Uuid = null ): UseProjectHook => {

    const history                         = useHistory(),
          location                        = useLocation(),
          modalActions                    = useSystemModals(),
          [ prefState, prefs, prefEnums ] = usePrefStates(),
          [ , projectSpecificPrefs ]      = usePrefStates( projectId ),
          { setProjectVersion }           = useContext( ProjectContext );

    useEffect( () => {}, [ prefState ] );

    const openProject: UseProjectHook['openProject'] = ( projectId ) => {

        let preferredRoute = prefs.get( prefEnums.PROJECT_SECTION ),
            defaultRoute   = 'PROJECT.PLANNING';

        try{

            // go to preferred, last known section of project
            history.push( RouterUtility.getRoute( preferredRoute, { projectId } ) );

        } catch( e ) {

            console.warn( `Project route "${ preferredRoute }" does not exist.` );

            // go to default route
            history.push( RouterUtility.getRoute( defaultRoute, { projectId } ) );
        }
    };

    const duplicateProject: UseProjectHook['duplicateProject'] = ( projectId, projectName, onComplete = null ) => {

        modalActions.show(
            <DuplicateProjectDialog
                projectId={ projectId }
                projectName={ projectName }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Project Duplicated Successfully' }
            />, {
                size: 'tiny'
            } );
    };

    const compareProjects: UseProjectHook['compareProjects'] = ( projectNames, title = 'Compare Projects', primaryProjectId = null ) => {

        modalActions.show(
            <CompareProjectDialog
                title={ title }
                projectIds={ projectNames }
                primaryProjectId={ primaryProjectId }
            />, {
                size:  'fullscreen',
                style: { maxWidth: `80vw` }
            }
        );
    };

    const getPlanningPageRoute: UseProjectHook['getPlanningPageRoute'] = ( projectId ) => {

        return RouterUtility.placeParamsInPath( `${ RouterUtility.getRoutePath( 'PROJECT.PLANNING' ) }`, { projectId } );

    };

    /* This method helps redirect users to the last subsection of an area they were at */
    const redirectToSubSectionWithin: UseProjectHook['redirectToSubSectionWithin'] = ( routePathKey ) => {

        if( !projectId ) return null;

        let subSections    = prefs.get( prefEnums.PROJECT_SUBSECTION ),
            preferredRoute = subSections[ routePathKey ] ?? null;

        if( !preferredRoute ) return null;

        let preferredRoutePath = RouterUtility.getRoute( preferredRoute, { projectId } ),
            isSameSubPath      = preferredRoutePath.includes( location.pathname ),
            isNotSameRoute     = preferredRoutePath !== location.pathname;

        try{

            // go to preferred, last known section of project area
            if( preferredRoute && isSameSubPath && isNotSameRoute ){

                console.log( { preferredRoute } );

                history.push( RouterUtility.getRoute( preferredRoute, { projectId } ) );
            }

        } catch( e ) {

            console.warn( `Project route "${ preferredRoute }" does not exist.` );
        }

    };

    const updateLastSubSection: UseProjectHook['updateLastSubSection'] = useCallback( ( routePathKey, routePath ) => {

        prefs.update( prefEnums.PROJECT_SUBSECTION, {

            ...prefs.get( prefEnums.PROJECT_SUBSECTION ),

            [ routePathKey ]: routePath
        } );

    }, [ prefState ] );

    const updateRootAndSubSection: UseProjectHook['updateRootAndSubSection'] = useCallback( ( routeBasePath, routeSubsectionPath ) => {

        prefs.update( null, {
            [ prefEnums.PROJECT_SECTION ]:    routeBasePath,
            [ prefEnums.PROJECT_SUBSECTION ]: {
                ...prefs.get( prefEnums.PROJECT_SUBSECTION ),
                [ routeBasePath ]: routeSubsectionPath,
            }
        } );

    }, [ prefState ] );

    const clearRootSection: UseProjectHook['clearRootSection'] = useCallback( () => {

        prefs.update( null, {
            [ prefEnums.PROJECT_SECTION ]: null,
        } );

    }, [ prefState ] );

    const getMaybeCurrentProjectVersionFromSet: UseProjectHook['getMaybeCurrentProjectVersionFromSet'] = ( projectVersions ) => {

        const projectVersionId = projectSpecificPrefs.get( prefEnums.PROJECT_VERSION_ID );

        return ProjectService.getProjectVersionById( projectVersions, projectVersionId, false )
               || ProjectService.getActiveProjectVersion( projectVersions )
               || ProjectService.getDefaultProjectVersion( projectVersions );
    };

    const createNewProjectVersion: UseProjectHook['createNewProjectVersion'] = ( projectId, projectVersionId, onComplete ) => {

        /* Launch project version creation modal */
        modalActions.show( (
            <CreateProjectVersionDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                onVersionCreated={ onComplete }
                onClose={ () => modalActions.toggle( false ) }
                successToastLabel={ 'New Project Version Created' }

            />
        ), {
            size:     'tiny',
            centered: false,
        } );
    };

    const showProjectVersionManagementDialog: UseProjectHook['showProjectVersionManagementDialog'] = ( projectId ) => {

        /* Launch project version management modal */
        modalActions.show( (
            <ManageProjectVersionsDialog
                projectId={ projectId }
                title={ 'Manage Project Versions' }
            />
        ), {
            size:     'tiny',
            centered: false,
        } );
    };

    const showProjectTemplateCreationDialog: UseProjectHook['showProjectTemplateCreationDialog'] = ( projectId, projectVersionId, onComplete ) => {

        modalActions.show( (
            <ProjectTemplateManagementDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                onComplete={ onComplete }
            />
        ), {
            size:     'small',
            centered: true,
        } );
    };

    const showProjectTemplatesManagementDialog: UseProjectHook['showProjectTemplatesManagementDialog'] = ( onComplete ) => {

        modalActions.show( (
            <ProjectTemplatesManagementDialog onComplete={ onComplete }/>
        ), {
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false
        } );
    };

    const showProjectTemplateEditDialog: UseProjectHook['showProjectTemplateEditDialog'] = ( templateId, onComplete ) => {

        modalActions.show( (
            <ProjectTemplateManagementDialog
                templateId={ templateId }
                onComplete={ onComplete }
            />
        ), {
            size:     'small',
            centered: true,
        } );
    };

    const showCreateProjectFromTemplateDialog: UseProjectHook['showCreateProjectFromTemplateDialog'] = ( onComplete ) => {

        modalActions.show( (
            <CreateProjectDialog
                onComplete={ onComplete }
            />
        ), {
            size:                 'large',
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false
        } );
    };

    const showProjectVersionMilestoneManagementDialog: UseProjectHook['showProjectVersionMilestoneManagementDialog'] = ( projectId, projectVersionId, noopMutation = false, onComplete = null ) => {

        modalActions.show(
            <ManageProjectVersionMilestonesDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                noopMutation={ noopMutation }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Updated Project Version Milestones Successfully!' }
            />, {
                // size: 'large'
                closeOnEscape:        false,
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
            } );
    };

    return {
        openProject,
        duplicateProject,
        compareProjects,
        getPlanningPageRoute,
        redirectToSubSectionWithin,
        updateLastSubSection,
        updateRootAndSubSection,
        clearRootSection,
        getMaybeCurrentProjectVersionFromSet,
        createNewProjectVersion,
        showProjectVersionManagementDialog,
        showProjectTemplateCreationDialog,
        showProjectTemplatesManagementDialog,
        showProjectTemplateEditDialog,
        showCreateProjectFromTemplateDialog,
        showProjectVersionMilestoneManagementDialog,
    };
};
